import { pickT } from "@repo/shared";
import { MultiBarChart } from "@/components/charts/multi-bar-chart";
import { MultiLineChart } from "@/components/charts/multi-line-chart";
import { useBrandColor } from "@/lib/context/brand-color-context";
import { useUserLanguage } from "@/lib/i18n/context";
import type { CostSnapshot } from "@/lib/types/flow";
import { getRelativeTime } from "@/lib/utils";
import { T } from "./cost-chart-widget.i18n";

type CostChartWidgetProps = {
  snapshot: CostSnapshot;
  displayMode?: "rolling" | "calendar";
  chartType?: "line" | "bar";
  language?: string;
  /** QP-208 — how many years of the simulated series to display (default 10). */
  projectionYears?: number;
};

export function CostChartWidget({
  snapshot,
  displayMode = "rolling",
  chartType = "line",
  language,
  projectionYears = 10,
}: CostChartWidgetProps) {
  const primaryColor = useBrandColor();
  const { userLanguage } = useUserLanguage();
  const lang = language || userLanguage;
  const t = pickT(T, lang);

  // Get data based on display mode, sliced to the configured display horizon
  // (the simulation always runs COST_SIMULATION_YEARS; display is a view on it)
  const fullData = displayMode === "rolling" ? snapshot.yearlyCostsRolling : snapshot.yearlyCosts;
  const yearlyData = fullData?.slice(0, Math.max(1, projectionYears));

  if (!yearlyData || yearlyData.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-muted-foreground">{t.noCostData}</p>
      </div>
    );
  }

  // Transform data for multi-line chart
  const chartData = yearlyData.map((entry) => ({
    x: displayMode === "rolling" ? `${t.yearPrefix} ${entry.period}` : entry.year || "",
    y1: entry.accountValueExCost, // Without cost (baseline)
    y2: entry.accountValue, // With cost
  }));

  const modeLabel = displayMode === "rolling" ? t.rollingMode : t.calendarMode;

  return (
    <div className="w-full space-y-4">
      <div className="w-full">
        {chartType === "bar" ? (
          <MultiBarChart
            data={chartData}
            height={280}
            line2Label={t.withCost}
            costLabel={t.withoutCost}
            primaryColor={primaryColor ?? undefined}
            lang={lang}
          />
        ) : (
          <MultiLineChart
            data={chartData}
            height={280}
            line1Label={t.withoutCost}
            line2Label={t.withCost}
            primaryColor={primaryColor ?? undefined}
            lang={lang}
          />
        )}
      </div>
      <div className="text-xs text-muted-foreground">
        <div>
          {modeLabel} • {t.currency}: {snapshot.currency}
        </div>
        {snapshot.timestamp && <div>{t.calculated(getRelativeTime(snapshot.timestamp, lang))}</div>}
      </div>
    </div>
  );
}
